import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle2, Phone, Clock, ArrowRight, Home } from 'lucide-react';

const ThankYouPage = () => {
  const navigate = useNavigate();

  useEffect(() => {
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'page_view', {
        page_title: 'Thank You - Richiesta Inviata',
        page_location: window.location.href,
        page_path: '/grazie'
      });
    }
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 py-4">
        <div className="max-w-7xl mx-auto px-6">
          <img
            src="https://customer-assets.emergentagent.com/job_smart-roofing-2/artifacts/7ngvxkwz_2.png"
            alt="Evoluzione Tetto"
            className="h-10 w-auto"
          />
        </div>
      </header>

      <div className="flex-1 flex items-center justify-center py-16 px-6 bg-slate-50">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-2xl w-full"
        >
          {/* Success */}
          <div className="text-center mb-10">
            <div className="w-20 h-20 bg-emerald-50 border border-emerald-200 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-10 h-10 text-emerald-500" />
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Outfit' }}>
              Richiesta Inviata con Successo
            </h1>
            <p className="text-slate-600 text-lg">Grazie per averci contattato</p>
          </div>

          {/* Steps */}
          <div className="bg-white border border-slate-200 rounded-2xl p-8 mb-8 shadow-sm">
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-[#FF5722] rounded-full flex items-center justify-center shrink-0 text-white font-bold text-sm">1</div>
                <div>
                  <h3 className="font-semibold text-slate-900 mb-1">Elaboriamo la tua richiesta</h3>
                  <p className="text-sm text-slate-600">Il nostro team sta gia analizzando i dettagli per prepararsi al sopralluogo.</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-[#FF5722] rounded-full flex items-center justify-center shrink-0 text-white font-bold text-sm">2</div>
                <div>
                  <h3 className="font-semibold text-slate-900 mb-1 flex items-center gap-2">
                    <Clock className="w-4 h-4 text-[#FF5722]" />
                    Ti contattiamo entro 24 ore
                  </h3>
                  <p className="text-sm text-slate-600">Un tecnico specializzato ti chiamara per fissare il sopralluogo gratuito.</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-[#FF5722] rounded-full flex items-center justify-center shrink-0 text-white font-bold text-sm">3</div>
                <div>
                  <h3 className="font-semibold text-slate-900 mb-1">Preventivo dettagliato</h3>
                  <p className="text-sm text-slate-600">Riceverai un preventivo completo e trasparente, senza impegno.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Contact */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
            <a
              href="tel:800864621"
              className="bg-white border border-slate-200 hover:border-[#FF5722]/30 rounded-xl p-5 flex items-center gap-4 transition-all hover:shadow-md"
            >
              <div className="w-10 h-10 bg-[#FF5722]/10 rounded-full flex items-center justify-center">
                <Phone className="w-5 h-5 text-[#FF5722]" />
              </div>
              <div>
                <p className="text-slate-900 font-medium text-sm">Chiamaci ora</p>
                <p className="text-slate-500 text-xs">800 86 46 21</p>
              </div>
            </a>
            <a
              href="https://wa.me/393791617599"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white border border-slate-200 hover:border-green-500/30 rounded-xl p-5 flex items-center gap-4 transition-all hover:shadow-md"
            >
              <div className="w-10 h-10 bg-green-50 rounded-full flex items-center justify-center">
                <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                </svg>
              </div>
              <div>
                <p className="text-slate-900 font-medium text-sm">WhatsApp</p>
                <p className="text-slate-500 text-xs">379 161 7599</p>
              </div>
            </a>
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={() => navigate('/')}
              className="inline-flex items-center justify-center gap-2 text-slate-700 hover:text-slate-900 border border-slate-200 hover:border-slate-300 px-6 py-3 rounded-full transition-all text-sm font-medium"
            >
              <Home className="w-4 h-4" />
              Torna alla Home
            </button>
            <button
              onClick={() => navigate('/lavorazioni')}
              className="inline-flex items-center justify-center gap-2 bg-[#FF5722] hover:bg-[#FF7043] text-white px-6 py-3 rounded-full transition-all text-sm font-bold"
            >
              Scopri le nostre lavorazioni
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </motion.div>
      </div>

      <footer className="bg-slate-900 border-t border-slate-800 py-6">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-xs text-slate-400">&copy; {new Date().getFullYear()} Evoluzione Tetto - Revogreen Srl. Tutti i diritti riservati.</p>
        </div>
      </footer>
    </div>
  );
};

export default ThankYouPage;
